/**
 * TaahhutnamePdfIndir.jsx — "PDF indir: Türkçe | English" for one project taahhütname (İSG / Elektrik / Statik), backlog #666.
 *
 * The PDF is made in the browser from GET /projects/{id}/taahhutnameler read in the language of the button (services/projeTaahhutnameService.js
 * + projeTaahhutnamePdf.js): the header, the source image the inspector chose, the wording in force, blank signature blocks. The two buttons
 * are the two languages of the PDF, whatever the UI language is. Shown only when the source was chosen and the wording is there (`pdfHazirMi`:
 * an organizer never gets the source file name, so never sees the buttons). Sits next to the print button of TaahhutnameBelgesi.
 */
import React, { useState } from "react";
import { Button, Spinner } from "reactstrap";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { projeTaahhutnameIndir, pdfHazirMi, PROJE_TAAHHUTNAME_DILLERI } from "../../services/projeTaahhutnameService";

/**
 * @param projectId the live project the taahhütname belongs to
 * @param taahhut   one entry of GET /projects/{id}/taahhutnameler (key, source, text)
 */
export default function TaahhutnamePdfIndir({ projectId, taahhut, className = "" }) {
  const { t } = useTranslation("pdf");
  const [yukleniyor, setYukleniyor] = useState(null);
  const [hata, setHata] = useState(null);
  const [uyari, setUyari] = useState(null);

  if (!projectId || !taahhut?.key || !pdfHazirMi(taahhut)) return null;

  async function indir(dil) {
    setYukleniyor(dil);
    setHata(null);
    setUyari(null);
    try {
      const uyarilar = await projeTaahhutnameIndir({ projectId, key: taahhut.key, dil });
      if (uyarilar?.metinTurkce) setUyari(t("projeTaahhutname.ui.metinTurkce"));
      else if (uyarilar?.logoSorunu) setUyari(t("projeTaahhutname.ui.logoOkunamadi"));
    } catch (e) {
      setHata(e?.message || String(e));
    } finally {
      setYukleniyor(null);
    }
  }

  const etiket = { tr: t("projeTaahhutname.ui.dilTr"), en: t("projeTaahhutname.ui.dilEn") };
  return (
    <div className={className} data-testid="proje-taahhutname-pdf">
      <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
        <span className="text-muted small">{t("projeTaahhutname.ui.indir")}</span>
        {PROJE_TAAHHUTNAME_DILLERI.map((dil) => (
          <Button
            key={dil} color="primary" size="sm" outline disabled={Boolean(yukleniyor)}
            onClick={() => indir(dil)} data-testid={`proje-taahhutname-pdf-${dil}`}
          >
            {yukleniyor === dil ? <Spinner size="sm" className="me-1" /> : <Download size={14} className="me-1" />}
            {yukleniyor === dil ? t("projeTaahhutname.ui.hazirlaniyor") : etiket[dil]}
          </Button>
        ))}
      </div>
      {hata && <div className="text-danger small text-end mt-1" data-testid="proje-taahhutname-pdf-hata">{t("projeTaahhutname.ui.hata", { hata })}</div>}
      {!hata && uyari && <div className="text-warning small text-end mt-1" data-testid="proje-taahhutname-pdf-uyari">{uyari}</div>}
    </div>
  );
}
